import { createContext, use, useMemo } from 'react';
import { QueryCache, QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keys, meQueryOptions } from './keys.js';

/** @type {import('react').Context<import('./index.js').BlimoApi|null>} */
const ApiContext = createContext(null);

/**
 * @param {{ api: import('./index.js').BlimoApi, children: import('react').ReactNode }} props
 */
export function ApiProvider({ api, children }) {
  return <ApiContext value={api}>{children}</ApiContext>;
}

export function useApi() {
  const api = use(ApiContext);
  if (!api) throw new Error('useApi() must be used inside <ApiProvider>');
  return api;
}

/** Current user (GET /me). */
export function useMe() {
  const api = useApi();
  return useQuery(meQueryOptions(api));
}

/**
 * QueryClient with sensible defaults. `onUnauthenticated` is called when any query
 * fails with 401 (session expired) – apps redirect to the login page there.
 * @param {{ onUnauthenticated?: (error: any) => void }} [options]
 */
export function createQueryClient({ onUnauthenticated } = {}) {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (/** @type {any} */ (error)?.status === 401 && query.queryKey[1] !== 'me') onUnauthenticated?.(error);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (count, error) => {
          const status = /** @type {any} */ (error)?.status;
          if (status && status >= 400 && status < 500) return false;
          return count < 2;
        },
      },
      mutations: { retry: false },
    },
  });
}

/**
 * Beendet die Impersonation. `onDone` erhält das wiederhergestellte Admin-Profil
 * (z. B. für die Rückkehr ins Verwaltungs-CMS aus dem Portal).
 * @param {{ onDone?: (me: any) => void }} [options]
 */
export function useStopImpersonation({ onDone } = {}) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.auth.stopImpersonation(),
    onSuccess: async (me) => {
      queryClient.setQueryData(keys.me(), me);
      await queryClient.invalidateQueries();
      onDone?.(me);
    },
  });
}

/** Returns a function that refetches /me (e.g. after enabling 2FA). */
export function useRefreshMe() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: keys.me() });
}

/**
 * Maps the Fortify endpoints onto the action interface of <TwoFactorSettings>.
 * Callers refresh /me via `onChanged` (see useRefreshMe).
 */
export function useTwoFactorActions() {
  const api = useApi();
  return useMemo(
    () => ({
      enable: () => api.auth.enableTwoFactor(),
      qrCode: () => api.auth.twoFactorQrCode(),
      confirm: (payload) => api.auth.confirmTwoFactor(payload),
      recoveryCodes: () => api.auth.twoFactorRecoveryCodes(),
      regenerateRecoveryCodes: () => api.auth.regenerateRecoveryCodes(),
      disable: () => api.auth.disableTwoFactor(),
      confirmPassword: (payload) => api.auth.confirmPassword(payload),
    }),
    [api],
  );
}

/**
 * Loads /me for a router `beforeLoad`. Never throws for the expected auth states.
 * @param {{ queryClient: QueryClient, api: import('./index.js').BlimoApi }} ctx
 * @returns {Promise<{ status: 'ok', me: import('./auth.js').Me } | { status: 'unauthenticated' } | { status: 'two-factor-required' }>}
 */
export async function loadMe({ queryClient, api }) {
  try {
    const me = await queryClient.ensureQueryData(meQueryOptions(api));
    return { status: 'ok', me };
  } catch (error) {
    if (/** @type {any} */ (error)?.status === 401) return { status: 'unauthenticated' };
    if (/** @type {any} */ (error)?.isTwoFactorRequired) return { status: 'two-factor-required' };
    throw error;
  }
}
